/**
 * PocketFiler - Help Center (public)
 * Categorized help articles with search.
 */

import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, FileText, Zap, Shield, Users, CreditCard, Globe, Mail, ChevronRight, ExternalLink } from 'lucide-react';
import { useTranslation } from '../i18n';


const CATEGORIES = [
  {
    id: 'getting-started',
    icon: Zap,
    title: 'Getting Started',
    description: 'Account setup, first steps, and basics',
    articles: [
      { id: 'signup', title: 'How to sign up and verify your email', body: 'Go to app.pocketfiler.com/signup. Enter your email, a secure password (8+ chars, mix of letters/numbers/symbols), and agree to the terms. Check your email for the verification link — click it and you\'re in.' },
      { id: 'first-contract', title: 'Create your first AI contract', body: 'From the dashboard, click "Create" or navigate to Contracts. Pick a template (NDA, SOW, MSA, etc.), fill in the parties\' details and terms, and click Generate. AI drafts it in seconds. Review, edit if needed, then send for signature.' },
      { id: 'invite-client', title: 'Onboard your first client', body: 'Go to Clients → "Invite Client". Enter their email and pick which documents you need (ID, proof of address, insurance card, bank statements, etc.). They get a secure link, upload everything in minutes, and AI verifies each document automatically.' },
      { id: 'dashboard', title: 'Understanding your dashboard', body: 'The dashboard shows what needs your attention: deals in progress, pending tasks, upcoming meetings, and AI-generated briefings. The setup checklist on the right tracks your onboarding progress.' },
    ],
  },
  {
    id: 'contracts',
    icon: FileText,
    title: 'Contracts & Documents',
    description: 'AI contracts, signatures, and document management',
    articles: [
      { id: 'ai-contracts', title: 'How AI contract generation works', body: 'PocketFiler uses Claude Opus 4.7 to draft contracts based on your inputs. Pick a template, fill in the fields (state, parties, dates, terms), and AI produces a legally structured contract. For high-value agreements, always have a lawyer review.' },
      { id: 'digital-sign', title: 'Digital signatures (ESIGN compliant)', body: 'Click "Send for Signature" on any contract, enter recipient emails, and send. Signers click a link, draw or type their signature, and we record IP + timestamp + email verification for a full audit trail.' },
      { id: 'translate', title: 'Translate contracts to 12 languages', body: 'Open any document in the Create Document editor. Click the blue "Translate" button in the toolbar, pick a target language, and AI translates the entire document while preserving formatting. Perfect for international deals.' },
      { id: 'upload', title: 'Uploading documents', body: 'From Documents, click Upload. Drag/drop or select files. Accepted: PDF, JPG, PNG, Word, Excel, PowerPoint, TXT, CSV. Max 50MB per file. AI can analyze and extract data automatically.' },
    ],
  },
  {
    id: 'team',
    icon: Users,
    title: 'Team & Collaboration',
    description: 'Invites, permissions, and deal rooms',
    articles: [
      { id: 'invite-team', title: 'Invite a team member', body: 'Settings → Organization → Invite Member. Enter their email and role (Admin, Member, or Viewer). They get an email with a signup link. Depending on your plan, you can invite 1-25+ members.' },
      { id: 'deal-rooms', title: 'Create a deal room', body: 'Deal rooms are shared workspaces for a specific deal. From Deals, click a deal and select "Open Deal Room". Add contracts, documents, meeting links, and invite external guests. Everything for that deal lives in one place.' },
      { id: 'video-calls', title: 'Built-in video meetings', body: 'Schedule a video meeting from Meetings → "Schedule Meeting". Invitees get an email with a join link — no Zoom account needed. Available on Small Business plan and above.' },
    ],
  },
  {
    id: 'security',
    icon: Shield,
    title: 'Security & Privacy',
    description: '2FA, sessions, and data protection',
    articles: [
      { id: '2fa', title: 'Enable two-factor authentication', body: 'Settings → Security → Enable 2FA. Scan the QR code with Google Authenticator or Authy, enter the 6-digit code to confirm, and save your backup codes somewhere safe. From then on, login requires the code in addition to your password.' },
      { id: 'sessions', title: 'Manage your active sessions', body: 'Settings → Security → Active Sessions. See every device currently logged in, including IP + last used time. Revoke any session, or click "Log out everywhere" to kill all sessions at once.' },
      { id: 'data-security', title: 'How your data is protected', body: 'TLS encryption in transit, AES-256 encryption at rest on AWS S3. Passwords hashed with bcrypt. JWT authentication with refresh token rotation. Full audit log of all actions. SOC 2 Type II readiness in progress.' },
    ],
  },
  {
    id: 'billing',
    icon: CreditCard,
    title: 'Plans & Billing',
    description: 'Subscriptions, upgrades, and invoices',
    articles: [
      { id: 'free-plan', title: 'What\'s included in Free?', body: 'Free forever: 1 user, 3 AI contracts/month, 500MB storage, digital signing, 8 templates, all 12 languages. Great for individuals testing the platform or making occasional contracts.' },
      { id: 'upgrade', title: 'Upgrade your plan', body: 'Settings → Plan → pick a tier. Payment handled by Stripe. Changes take effect immediately. Annual billing saves 20% vs monthly.' },
      { id: 'cancel', title: 'Cancel or downgrade', body: 'Settings → Plan → Manage Subscription. You can cancel anytime — your plan stays active until the end of the current billing period. 14-day money-back guarantee on all paid plans.' },
      { id: 'refund', title: '14-day money-back guarantee', body: 'If PocketFiler doesn\'t replace the tools you were using, email info@pocketfiler.com within 14 days of your first paid charge for a full refund. No questions asked.' },
    ],
  },
  {
    id: 'international',
    icon: Globe,
    title: 'International Trade',
    description: 'Cross-border deals and language features',
    articles: [
      { id: 'cross-border', title: 'Cross-border deal rooms', body: 'Create a deal room, invite overseas counterparties, and use the built-in translation features. Contracts can be generated in both languages. Document uploads support all common file types. Video meetings have live captions in 40+ languages.' },
      { id: 'languages', title: 'Using PocketFiler in your preferred language', body: 'Settings → Profile → Language. Pick from English, Spanish, French, German, Portuguese, Arabic, Hindi, Japanese, Korean, Russian, Simplified Chinese, or Vietnamese. The entire interface translates instantly.' },
    ],
  },
];


export default function HelpPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeArticle, setActiveArticle] = useState(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return CATEGORIES;
    const q = query.toLowerCase();
    return CATEGORIES.map(cat => ({
      ...cat,
      articles: cat.articles.filter(a =>
        a.title.toLowerCase().includes(q) || a.body.toLowerCase().includes(q)
      )
    })).filter(cat => cat.articles.length > 0);
  }, [query]);

  // Detail view
  if (activeArticle) {
    const article = CATEGORIES
      .flatMap(c => c.articles.map(a => ({ ...a, category: c })))
      .find(a => a.id === activeArticle);
    if (!article) return null;

    return (
      <div className="min-h-screen bg-background">
        <header className="border-b">
          <div className="container mx-auto px-4 py-4">
            <button
              onClick={() => { setActiveArticle(null); }}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to help center
            </button>
          </div>
        </header>
        <div className="container mx-auto px-4 py-12 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <article.category.icon className="h-3 w-3" />
            {article.category.title}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{article.title}</h1>
          <div className="text-base text-muted-foreground leading-relaxed whitespace-pre-line">
            {article.body}
          </div>
          <div className="mt-12 pt-8 border-t">
            <h3 className="font-semibold mb-2">Still stuck?</h3>
            <p className="text-sm text-muted-foreground mb-3">Our team is fast — reach out any time.</p>
            <a href="mailto:info@pocketfiler.com" className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
              <Mail className="h-4 w-4" /> info@pocketfiler.com
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
          <a href="mailto:info@pocketfiler.com" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5">
            <Mail className="h-4 w-4" /> Contact us
          </a>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-5xl font-bold mb-3">Help Center</h1>
          <p className="text-lg text-muted-foreground mb-6">
            Answers, guides, and tutorials for everything PocketFiler.
          </p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search help articles..."
              className="w-full pl-11 pr-4 py-3 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p className="mb-2">No articles matched "{query}"</p>
            <a href="mailto:info@pocketfiler.com" className="text-sm text-primary hover:underline">
              Email our team — we\'ll help directly.
            </a>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {filtered.map(cat => {
              const Icon = cat.icon;
              return (
                <div key={cat.id} className="rounded-xl border bg-background p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-bold text-lg">{cat.title}</h2>
                      <p className="text-sm text-muted-foreground">{cat.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-1">
                    {cat.articles.map(a => (
                      <li key={a.id}>
                        <button
                          onClick={() => setActiveArticle(a.id)}
                          className="w-full text-left flex items-center justify-between py-2 px-2 rounded-md hover:bg-muted/50 text-sm group"
                        >
                          <span className="flex-1">{a.title}</span>
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {/* API Docs link */}
        <div className="mt-12 rounded-xl border bg-muted/30 p-5 text-center">
          <h3 className="font-semibold mb-1">Building an integration?</h3>
          <p className="text-sm text-muted-foreground mb-3">
            Full REST API with authentication, webhooks, and Zapier support.
          </p>
          <Link to="/developers" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
            View API docs <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
